import React, { useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { verifyCode, resendVerificationCode, clearVerificationError } from "../../redux/verificationSlice"; // Doğru importlar

const VerificationForm = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const inputsRef = useRef([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [timer, setTimer] = useState(120);
  const [phoneNumber, setPhoneNumber] = useState("");
  const dispatch = useDispatch();
  const { loading, error: reduxError, resendCodeLoading } = useSelector((state) => state.verification); 

  useEffect(() => {
    if (timer > 0) {
      const countdown = setTimeout(() => setTimer(timer - 1), 1000);
      return () => clearTimeout(countdown);
    }
  }, [timer]);

  useEffect(() => {
    const storedPhoneNumber = localStorage.getItem("phoneNumber") || "No Number";
    setPhoneNumber(storedPhoneNumber);
  }, []);

  useEffect(() => {
    return () => {
      dispatch(clearVerificationError()); // Doğru clearVerificationError
    };
  }, [dispatch]);

  const handleInputChange = (e, index) => {
    const value = e.target.value;
    if (/^\d$/.test(value)) {
      if (index < inputsRef.current.length - 1) {
        inputsRef.current[index + 1].focus();
      }
    } else {
      e.target.value = "";
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !e.target.value && index > 0) {
      inputsRef.current[index - 1].focus();
    }
  };

  const handleResendCode = async () => {
    if (timer > 0) return;

    dispatch(resendVerificationCode()) // Doğru resendVerificationCode
      .unwrap()
      .then(() => {
        alert("Verification code has been resent.");
        setTimer(120);
        setErrorMessage("");
      })
      .catch((error) => {
        setErrorMessage(error || "Code could not be resent.");
      });
  };

  const handleVerifyCode = async () => {
    const code = inputsRef.current.map((input) => input.value).join("");

    if (code.length !== 6) {
      setErrorMessage("Please enter a 6-digit code.");
      return;
    }

    dispatch(verifyCode(code)) // Doğru verifyCode
      .unwrap()
      .then(() => {
        alert("Phone number has been successfully verified!");
        navigate("/questionInfo");
      })
      .catch((error) => {
        setErrorMessage(error || "Verification failed.");
      });
  };

  return (
    <div className="bg-tertiary max-w-[90%] md:w-[600px] lg:w-[1033px] mx-auto mt-12 md:mt-24 p-6 md:p-8 rounded-[20px] md:rounded-[30px] shadow-lg">
      <button
        className="flex items-center justify-center w-[90px] md:w-[117px] h-[35px] md:h-[39px] border-2 rounded-[30px] text-text bg-white font-poppins text-[12px] md:text-[14px] mb-6 md:mb-8"
        onClick={() => navigate("/signUp")}
      >
        <span className="w-[20px] md:w-[24px] h-[20px] md:h-[24px] flex items-center justify-center">
          ❮
        </span>
        {t("goToBack")}
      </button>
      <p className="text-center text-text font-poppins text-[20px] md:text-[24px] mt-[30px] md:mt-[49px] mb-3 md:mb-4 leading-6">
        {t("verificationTitle")} <br />
        <span className="font-bold">{phoneNumber}</span>
      </p>
      <div className="flex justify-center">
        {Array.from({ length: 6 }).map((_, index) => (
          <input
            key={index}
            type="text"
            maxLength="1"
            ref={(el) => (inputsRef.current[index] = el)}
            onChange={(e) => handleInputChange(e, index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            className={`w-[50px] md:w-[80px] lg:w-[97px] h-[50px] md:h-[61px] text-center text-[20px] md:text-[24px] font-bold border-[2px] border-black ${
              index === 0 ? "rounded-l-[15px] md:rounded-l-[30px]" : ""
            } ${index === 5 ? "rounded-r-[15px] md:rounded-r-[30px]" : ""} focus:outline-none focus:ring-2 focus:ring-[#1B5E20]`}
          />
        ))}
      </div>

      {/* Kalan Süre */}
      <p className="text-center text-[#454545] font-poppins text-[16px] md:text-[20px] mb-4 md:mb-6 mt-4 md:mt-[22px]">
        {t("enterCode")}{" "}
        <span className="font-poppins font-[700] text-[#454545]">{timer}</span>
      </p>
      {(errorMessage || reduxError) && (
        <p className="text-center text-red-500 text-[16px] md:text-[18px] mt-2">
          {errorMessage || reduxError}
        </p>
      )}
      <button
        className={`w-full md:w-[600px] lg:w-[973px] h-[50px] md:h-[61px] bg-button-bg text-white text-[18px] md:text-[20px] font-poppins rounded-full mb-4 md:mb-6 hover:bg-[#144d15] transition ${resendCodeLoading ? 'opacity-50 cursor-not-allowed' : ''}`} // Doğru resendCodeLoading
        onClick={handleVerifyCode}
        disabled={loading} // Doğru loading state'i
      >
        {loading ? "Verifying..." : t("verifyAccount")}
      </button>
      <p
        className={`text-center text-text font-poppins text-[18px] md:text-[20px] mb-6 md:mb-8 cursor-pointer ${
          timer === 0 && !resendCodeLoading ? "hover:underline" : "text-gray-400 cursor-not-allowed" // Doğru resendCodeLoading
        }`}
        onClick={handleResendCode}
        style={{ pointerEvents: resendCodeLoading ? 'none' : 'auto' }} // Doğru resendCodeLoading
      >
        {resendCodeLoading ? "Resending..." : t("resendCode")}
      </p>
      <div className="flex flex-col items-center">
        <hr className="w-full border-text mb-3 md:mb-4" />
        <p className="text-center text-[#454545] text-[14px] md:text-[15px] font-poppins">
          {t("didntGetCode")}{" "}
          <button
            className="text-text font-poppins ml-[15px] md:ml-[23px] font-[700] cursor-pointer hover:underline"
            onClick={() => navigate("/signUp")}
          >
            {t("changePhoneNumber")}
          </button>
        </p>
      </div>
    </div>
  );
};

export default VerificationForm;
